import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Flex, Form, Layout, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomRangePicker from 'src/app/shared/components/common/range-picker'
import CustomSelectAll from 'src/app/shared/components/common/select-all'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { useDatePicker } from 'src/app/shared/hooks/useDatePicker'
import { useDisableFutureDates } from 'src/app/shared/hooks/useDisableFutureDates'
import { getListErrorReasons, getListRecipients } from 'src/app/shared/services/BackgroundService'
import { StringSetAsArray } from 'src/app/shared/utils'
import { colorBgBrandless, colorPrimary } from 'src/app/themes/primaryThemeDefault'
import { ArrowRightIcon, CalendarCustomIcon, InfoIcon, RefreshIcon, WhiteSearchIcon } from 'src/assets/icons'
import { SentEmailTextLogStatus } from '..'
import { generateValidationSchema } from '../utils/ultil'

const SentEmailTextsErrorLogs: React.FC = () => {
  const { getCurrentDate, timeZoneId } = useDatePicker()

  const { t } = useTranslation()
  const defaultValues = {
    messageDate: [getCurrentDate(), getCurrentDate()],
    recipients: [null],
    errorReasons: [null],
    status: SentEmailTextLogStatus.Failed,
  }

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    trigger,
    resetField,
  } = useForm<any>({ mode: 'all', defaultValues, resolver: yupResolver(generateValidationSchema(t)) })
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const [isGettingListRecipients, setIsGettingListRecipients] = useState<boolean>()
  const [isGettingListErrorReasons, setIsGettingListErrorReasons] = useState<boolean>()
  const [listRecipients, setListRecipients] = useState<any[]>([])
  const [listErrorReasons, setListErrorReasons] = useState([])
  const retentionSettings = useSelector((state: any) => state?.masterSetup?.deferredConfig?.retentionSettings)
  const { messageRetention } = useMemo(() => retentionSettings, [retentionSettings])
  const { disableFutureDates } = useDisableFutureDates()

  const onSubmit = useCallback(
    async (values: any) => {
      try {
        setLoading(true)

        const formatDateString = (dateString: any) => dayjs(dateString).tz(timeZoneId).format('YYYY-MM-DD')
        const listRecipientsResponse = await getListRecipients()

        let payload = {
          messageDateFrom: values?.messageDate?.[0] ? formatDateString(values?.messageDate?.[0]) : '',
          messageDateTo: values?.messageDate?.[1] ? formatDateString(values?.messageDate?.[1]) : '',
          recipients: values?.recipients?.includes(null) ? listRecipientsResponse?.data?.email : values?.recipients,
          errorReasons: values?.errorReasons?.includes(null) ? ['all-reason'] : values?.errorReasons,
          subjects: [],
          status: values?.status,
        }

        navigate(menuUrls.sentEmailTextsResults, {
          state: {
            sentEmailLog: payload,
          },
        })
      } catch (error) {
        console.log('ERROR: ', error)
      } finally {
        setLoading(false)
      }
    },
    [navigate, timeZoneId],
  )

  const resetSentEmailLog = () => {
    resetField('messageDate')
    resetField('recipients')
    resetField('errorReasons')
  }
  useEffect(() => {
    setValue('messageDate', [getCurrentDate(), getCurrentDate()])
    setValue('recipients', [null])
    setValue('errorReasons', [null])
    trigger()
  }, [])

  const handleGetListRecipients = useCallback(async () => {
    // get list recipients
    try {
      setIsGettingListRecipients(true)
      const listResponse = await getListRecipients()
      if (listResponse.statusCode === STATUS_CODES?.SUCCESS) {
        setListRecipients(StringSetAsArray(listResponse.data))
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsGettingListRecipients(false)
    }
  }, [])

  const handleGetListErrorReasons = useCallback(async () => {
    // get list  Error reasons
    try {
      setIsGettingListErrorReasons(true)
      const listResponse = await getListErrorReasons()
      if (listResponse.statusCode === STATUS_CODES?.SUCCESS) {
        setListErrorReasons(listResponse.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsGettingListErrorReasons(false)
    }
  }, [])

  useEffect(() => {
    window.localStorage.removeItem('sentEmailLog')
    handleGetListRecipients()
    handleGetListErrorReasons()
  }, [])

  return (
    <>
      <Layout.Content
        style={{
          padding: 24,
        }}
        className='issued-check-processing-log'
      >
        <Form onFinish={handleSubmit(onSubmit)}>
          <Flex
            style={{
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <Controller
              control={control}
              name='messageDate'
              render={({ field, fieldState }) => (
                <CustomRangePicker
                  label={t('SentEmailLog:sentEmailLog.filed.messageDate')}
                  value={field.value}
                  onChange={async (e: any) => {
                    field.onChange(e)
                  }}
                  onBlur={async () => {
                    field.onBlur()
                    await trigger('date')
                  }}
                  error={fieldState.error}
                  placeholder={[
                    t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.dateFrom'),
                    t('IssuedCheckProcessingLog:issued_check_processing_log.placeholder.dateTo'),
                  ]}
                  separator={<ArrowRightIcon className='issued-check-log-separator' />}
                  suffixIcon={<CalendarCustomIcon />}
                  required
                  disabledDate={disableFutureDates}
                  inputReadOnly
                />
              )}
            />
            <Controller
              control={control}
              name='recipients'
              render={({ field, fieldState }) => (
                <CustomSelectAll
                  required
                  label={t('SentEmailLog:sentEmailLog.filed.recipients')}
                  placeholder={t('SentEmailLog:sentEmailLog.placeholder.selectUsers')}
                  value={field.value}
                  onChange={(data: any) => {
                    if (!data || data?.length === 0) {
                      setValue('recipients', [null])
                    } else if (data?.includes(null)) {
                      if (!data[0]) {
                        const newData = data?.slice(1)
                        setValue('recipients', [...newData])
                      } else {
                        setValue('recipients', [null])
                      }
                    } else {
                      setValue('recipients', data)
                    }
                  }}
                  onBlur={field.onBlur}
                  error={fieldState.error}
                  options={
                    listRecipients?.map((item) => ({
                      label: item.recipientName,
                      value: item.email,
                    })) ?? []
                  }
                  mode='multiple'
                  defaultActiveFirstOption
                  allowClear={!field.value?.includes(null)}
                  loading={isGettingListRecipients}
                  defaultOption={{
                    label: 'All Users',
                    value: null,
                  }}
                  maxTagCount={'responsive'}
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                      <span>+ {omittedValues.length}...</span>
                    </Tooltip>
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name='errorReasons'
              render={({ field, fieldState }) => {
                return (
                  <CustomSelectAll
                    label={t('SentEmailLog:sentEmailLog.filed.errorReasons')}
                    placeholder={t('SentEmailLog:sentEmailLog.placeholder.selectErrorReasons')}
                    value={field.value}
                    onChange={(data: any) => {
                      if (!data || data?.length === 0) {
                        setValue('errorReasons', [null])
                      } else if (data?.includes(null)) {
                        if (!data[0]) {
                          const newData = data?.slice(1)
                          setValue('errorReasons', [...newData])
                        } else {
                          setValue('errorReasons', [null])
                        }
                      } else {
                        setValue('errorReasons', data)
                      }
                    }}
                    onBlur={field.onBlur}
                    error={fieldState.error}
                    options={
                      listErrorReasons?.map((item) => ({
                        label: item,
                        value: item,
                      })) ?? []
                    }
                    mode='multiple'
                    defaultActiveFirstOption
                    allowClear={!field.value?.includes(null)}
                    loading={isGettingListErrorReasons}
                    defaultOption={{
                      label: 'All Error Reasons',
                      value: null,
                    }}
                    maxTagCount={'responsive'}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                        <span>+ {omittedValues.length}...</span>
                      </Tooltip>
                    )}
                    turnOffSort={true}
                  />
                )
              }}
            />

            <div
              style={{
                borderColor: colorPrimary,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 12,
                backgroundColor: colorBgBrandless,
                padding: '12px 20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <InfoIcon width={28} height={28} />
              <Typography
                style={{
                  fontSize: 16,
                  fontFamily: 'MyriadPro',
                }}
              >
                {t('SentEmailLog:sentEmailLog.filed.noteMessagingHistoryIsRetainedWithinTheSystemFor90Days', {
                  retentionDay: `${messageRetention} ${messageRetention > 1 ? t('common:days') : t('common:day')}`,
                })}
              </Typography>
            </div>
            <Flex
              style={{
                justifyContent: 'flex-end',
                gap: 12,
              }}
              className='check-button-group'
            >
              <Button
                className='reset-btn'
                disabled={!isValid || !isEmpty(errors) || loading}
                onClick={resetSentEmailLog}
                icon={<RefreshIcon />}
              >
                {t('IssuedCheckProcessingLog:issued_check_processing_log.reset')}
              </Button>
              <Button
                type='primary'
                className='search-check-btn'
                disabled={!isValid || !isEmpty(errors) || loading}
                htmlType='submit'
                loading={loading}
                icon={<WhiteSearchIcon />}
              >
                {t('IssuedCheckProcessingLog:issued_check_processing_log.search_btn')}
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Layout.Content>
    </>
  )
}

export default SentEmailTextsErrorLogs
